import * as React from 'react';

import type { ParameterView } from '@/api/parameters-client';
import { useParametersList, useUpdateParameter } from '@/api/use-parameters';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  buildParametersCsv,
  formatValue,
  isSensitiveParameter,
  paramTypeLabel,
  paramUnit,
  parametersCsvFileName,
  rangeHint,
} from '@/lib/param-format';
import { ParameterChangelog } from '@/routes/parameters/parameter-changelog';
import { ParameterEditForm } from '@/routes/parameters/parameter-edit-form';
import { SensitiveConfirmDialog } from '@/routes/parameters/sensitive-confirm-dialog';

// =============================================================================
// parameters-screen.tsx — Écran A-24 (Application Parameters, AdminFull, §2.8).
// =============================================================================
// [S2-TRANCHÉ #4] : liste + édition validée contre valid_range (autorité API) +
// historique read-only. PAS de workflow 2 étapes (V1.5). [S2-TRANCHÉ #5] : app
// admin-only GLOBAL. [S2-TRANCHÉ #7] : LOCAL (double Option B).
//
// §2.8 (S2-FRONT-ADMIN) — organisation du volet paramètres :
//   - VOLET CATÉGORIES : filtrage par catégorie (boutons bascule aria-pressed).
//   - RECHERCHE        : filtre plein-texte (clé / description / catégorie).
//   - BADGE « Modifié »: signale value ≠ defaultValue (texte, jamais couleur seule).
//   - RESTAURER DÉFAUT : réapplique defaultValue via le MÊME PATCH que l'édition
//                        (aucun endpoint back nouveau — value := defaultValue).
//
// Filtres de VUE (recherche, catégorie) = état in-memory React (RULE_REF_006 :
// pas de localStorage). La SÉLECTION d'un paramètre reste en état d'URL (search
// param `param`), reçue en props pour rester testable hors routeur.

/** Un paramètre est « modifié » si sa valeur courante diffère de son défaut. */
function isModified(param: ParameterView): boolean {
  return param.value !== param.defaultValue;
}

export interface ParametersScreenProps {
  /** Clé du paramètre sélectionné (état d'URL), ou null si aucun. */
  readonly selectedKey: string | null;
  /** Met à jour la sélection (écrit le search param d'URL). */
  readonly onSelect: (paramKey: string | null) => void;
}

export function ParametersScreen({
  selectedKey,
  onSelect,
}: ParametersScreenProps): React.JSX.Element {
  const list = useParametersList();
  const [search, setSearch] = React.useState('');
  /** Catégorie filtrée (null = toutes). */
  const [category, setCategory] = React.useState<string | null>(null);
  /** Filtre rapide « Afficher uniquement les paramètres modifiés » (§2.8 l.1946). */
  const [modifiedOnly, setModifiedOnly] = React.useState(false);

  const params = React.useMemo(() => list.data ?? [], [list.data]);

  /** Catégories distinctes, triées (source du volet catégories). */
  const categories = React.useMemo(() => {
    const set = new Set(params.map((p) => p.category));
    return [...set].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [params]);

  const filtered = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    return params.filter((p) => {
      if (category !== null && p.category !== category) {
        return false;
      }
      if (modifiedOnly && !isModified(p)) {
        return false;
      }
      if (needle === '') {
        return true;
      }
      return (
        p.paramKey.toLowerCase().includes(needle) ||
        p.description.toLowerCase().includes(needle) ||
        p.category.toLowerCase().includes(needle)
      );
    });
  }, [params, search, category, modifiedOnly]);

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Paramètres applicatifs
        </h1>
        <p className="text-muted-foreground">
          Administration des paramètres globaux. Chaque valeur est validée par le
          serveur avant enregistrement.
        </p>
        {/* Ligne générique d'information (§2.8 l.1936, R28-CONFIRM-GENERIC-LINE) :
            rappelle la règle de confirmation pour TOUS les paramètres, en complément
            (pas en remplacement) de la confirmation renforcée (type-to-confirm) déjà
            appliquée aux seuls paramètres sensibles. */}
        <p className="text-sm text-muted-foreground">
          Toute modification est journalisée et prend effet à l&apos;enregistrement ;
          les paramètres sensibles requièrent une confirmation renforcée.
        </p>
      </header>

      {list.isSuccess ? (
        <div className="space-y-4">
          <div className="max-w-md space-y-2">
            <Label htmlFor="param-search">Rechercher un paramètre</Label>
            <Input
              id="param-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Clé, description ou catégorie…"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="param-modified-only"
              type="checkbox"
              checked={modifiedOnly}
              onChange={(e) => setModifiedOnly(e.target.checked)}
              className="h-4 w-4 rounded border-input text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <Label htmlFor="param-modified-only">
              Afficher uniquement les paramètres modifiés
            </Label>
          </div>

          <CategoryPanel
            categories={categories}
            selected={category}
            onSelect={setCategory}
          />

          <ExportCsvButton params={params} />
        </div>
      ) : null}

      <ParametersList
        list={list}
        items={filtered}
        totalCount={params.length}
        selectedKey={selectedKey}
        onSelect={onSelect}
      />

      {selectedKey !== null && list.isSuccess ? (
        <ParameterDetail
          parameter={params.find((p) => p.paramKey === selectedKey) ?? null}
          onClose={() => onSelect(null)}
        />
      ) : null}
    </div>
  );
}

interface ExportCsvButtonProps {
  /** Référentiel complet chargé (l'export porte sur TOUS les paramètres, §2.8 l.1950). */
  readonly params: readonly ParameterView[];
}

/**
 * Export CSV du référentiel de paramètres (§2.8 l.1950), généré CÔTÉ FRONT à partir
 * des données déjà chargées (aucun endpoint back). La sérialisation applique
 * l'anti-injection-formule OBS-1 (voir buildParametersCsv). Le fichier est produit
 * via un Blob + lien temporaire (BOM UTF-8 pour compat tableur FR). Aucune écriture
 * disque persistante, aucune dépendance.
 */
function ExportCsvButton({ params }: ExportCsvButtonProps): React.JSX.Element {
  const onExport = (): void => {
    const csv = buildParametersCsv(params);
    // BOM UTF-8 : garantit l'interprétation des accents par Excel (FR).
    const blob = new Blob([`﻿${csv}`], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = parametersCsvFileName();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onExport}
      disabled={params.length === 0}
    >
      Exporter en CSV
    </Button>
  );
}

interface CategoryPanelProps {
  readonly categories: readonly string[];
  readonly selected: string | null;
  readonly onSelect: (category: string | null) => void;
}

/** Volet catégories (§2.8) : boutons bascule (aria-pressed) — « Toutes » + une par catégorie. */
function CategoryPanel({
  categories,
  selected,
  onSelect,
}: CategoryPanelProps): React.JSX.Element {
  return (
    <div
      role="group"
      aria-label="Filtrer par catégorie"
      className="flex flex-wrap gap-2"
    >
      <Button
        type="button"
        size="sm"
        variant={selected === null ? 'secondary' : 'outline'}
        aria-pressed={selected === null}
        onClick={() => onSelect(null)}
      >
        Toutes les catégories
      </Button>
      {categories.map((cat) => {
        const isActive = selected === cat;
        return (
          <Button
            key={cat}
            type="button"
            size="sm"
            variant={isActive ? 'secondary' : 'outline'}
            aria-pressed={isActive}
            onClick={() => onSelect(isActive ? null : cat)}
          >
            {cat}
          </Button>
        );
      })}
    </div>
  );
}

interface ParametersListProps {
  readonly list: ReturnType<typeof useParametersList>;
  /** Sous-ensemble filtré (recherche + catégorie) effectivement affiché. */
  readonly items: readonly ParameterView[];
  /** Effectif total (avant filtrage) — pour distinguer « vide » de « aucun résultat ». */
  readonly totalCount: number;
  readonly selectedKey: string | null;
  readonly onSelect: (paramKey: string) => void;
}

function ParametersList({
  list,
  items,
  totalCount,
  selectedKey,
  onSelect,
}: ParametersListProps): React.JSX.Element {
  if (list.isPending) {
    return (
      <p role="status" className="text-muted-foreground">
        Chargement des paramètres…
      </p>
    );
  }
  if (list.isError) {
    return (
      <p role="alert" className="text-destructive">
        Erreur de chargement : {list.error.message}
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <p role="status" className="text-muted-foreground">
        {totalCount === 0
          ? 'Aucun paramètre à afficher.'
          : 'Aucun paramètre ne correspond à la recherche.'}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="ds-table w-full border-collapse text-sm">
        <caption className="sr-only">
          Liste des paramètres applicatifs (clé, type, unité, valeur courante,
          valeur par défaut, plage, catégorie, état de modification)
        </caption>
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
            <th scope="col" className="px-4 py-3 font-medium">
              Paramètre
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Type
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Unité
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Valeur courante
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Valeur par défaut
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Plage / contrainte
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Catégorie
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              <span className="sr-only">Action</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((param) => {
            const isSelected = param.paramKey === selectedKey;
            const modified = isModified(param);
            return (
              <tr
                key={param.paramKey}
                aria-selected={isSelected}
                className="border-b border-border last:border-b-0 aria-selected:bg-accent/40"
              >
                <td className="px-4 py-3 align-top">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium text-foreground">
                      {param.paramKey}
                    </span>
                    {/* Badge « Modifié » : LIBELLÉ texte (jamais la couleur seule). */}
                    {modified ? <Badge variant="accent">Modifié</Badge> : null}
                  </div>
                  <p className="mt-1 max-w-md text-xs text-muted-foreground">
                    {param.description}
                  </p>
                </td>
                <td className="px-4 py-3 align-top">
                  {/* Code couleur (badge) TOUJOURS doublé du libellé texte. */}
                  <Badge variant="outline">{paramTypeLabel(param.paramType)}</Badge>
                </td>
                <td className="px-4 py-3 align-top text-muted-foreground">
                  {paramUnit(param) || '—'}
                </td>
                <td className="px-4 py-3 align-top font-mono">
                  {formatValue(param.value)}
                </td>
                <td className="px-4 py-3 align-top font-mono text-muted-foreground">
                  {formatValue(param.defaultValue)}
                </td>
                <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                  {rangeHint(param.paramType, param.validRange)}
                </td>
                <td className="px-4 py-3 align-top">
                  <Badge variant="secondary">{param.category}</Badge>
                </td>
                <td className="px-4 py-3 align-top text-right">
                  <Button
                    type="button"
                    variant={isSelected ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => onSelect(param.paramKey)}
                    aria-label={`Modifier le paramètre ${param.paramKey}`}
                  >
                    {isSelected ? 'Sélectionné' : 'Modifier'}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface ParameterDetailProps {
  readonly parameter: ParameterView | null;
  readonly onClose: () => void;
}

function ParameterDetail({
  parameter,
  onClose,
}: ParameterDetailProps): React.JSX.Element {
  if (parameter === null) {
    return (
      <p role="alert" className="text-destructive">
        Paramètre introuvable.
      </p>
    );
  }

  return (
    <Card aria-labelledby="detail-title">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CardTitle id="detail-title" className="font-mono">
                {parameter.paramKey}
              </CardTitle>
              {isModified(parameter) ? (
                <Badge variant="accent">Modifié</Badge>
              ) : null}
            </div>
            <CardDescription>{parameter.description}</CardDescription>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-8">
        <section aria-labelledby="edit-heading" className="space-y-4">
          <h2 id="edit-heading" className="text-lg font-medium">
            Modifier la valeur
          </h2>
          <ParameterEditForm parameter={parameter} />
          <RestoreDefault parameter={parameter} />
        </section>

        <section aria-labelledby="history-heading" className="space-y-4">
          <h2 id="history-heading" className="text-lg font-medium">
            Historique des modifications
          </h2>
          <ParameterChangelog parameter={parameter} />
        </section>
      </CardContent>
    </Card>
  );
}

interface RestoreDefaultProps {
  readonly parameter: ParameterView;
}

/**
 * Restaurer la valeur par défaut (§2.8). Réutilise le PATCH d'édition (value :=
 * defaultValue) — AUCUN endpoint back nouveau. Désactivé (rendu informatif) quand
 * la valeur courante correspond déjà au défaut. L'autorité de validation reste
 * l'API : le message d'erreur serveur est relayé tel quel (role="alert").
 */
function RestoreDefault({ parameter }: RestoreDefaultProps): React.JSX.Element {
  const update = useUpdateParameter();
  const sensitive = isSensitiveParameter(parameter);
  // Confirmation avant restauration d'un paramètre sensible (§2.8 l.1936 — toute
  // modification sensible exige une confirmation textuelle).
  const [confirming, setConfirming] = React.useState(false);

  if (!isModified(parameter)) {
    return (
      <p className="text-sm text-muted-foreground">
        La valeur courante correspond déjà au défaut (
        {formatValue(parameter.defaultValue)}).
      </p>
    );
  }

  const restore = (): void => {
    update.mutate({
      paramKey: parameter.paramKey,
      input: {
        value: parameter.defaultValue,
        reason: 'Restauration de la valeur par défaut',
      },
    });
  };

  const onRestore = (): void => {
    if (sensitive) {
      setConfirming(true);
      return;
    }
    restore();
  };

  return (
    <div className="space-y-2 border-t border-border pt-4">
      <p className="text-sm text-muted-foreground">
        Valeur par défaut :{' '}
        <span className="font-mono text-foreground">
          {formatValue(parameter.defaultValue)}
        </span>
      </p>
      <Button
        type="button"
        variant="outline"
        onClick={onRestore}
        disabled={update.isPending}
      >
        {update.isPending
          ? 'Restauration…'
          : 'Restaurer la valeur par défaut'}
      </Button>
      <SensitiveConfirmDialog
        open={confirming}
        paramKey={parameter.paramKey}
        sensitive={sensitive}
        title="Restaurer la valeur par défaut"
        description={`Vous allez restaurer « ${parameter.paramKey} » à sa valeur par défaut (${formatValue(parameter.defaultValue)}).`}
        confirmLabel="Restaurer le défaut"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          restore();
          setConfirming(false);
        }}
      />
      <div aria-live="polite" aria-atomic="true">
        {update.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Restauration refusée : {update.error.message}
          </p>
        ) : null}
        {update.isSuccess ? (
          <p role="status" className="text-sm text-foreground">
            Valeur restaurée au défaut.
          </p>
        ) : null}
      </div>
    </div>
  );
}
